import React from "react";
import { Helmet } from "react-helmet";
import { Link } from "react-router-dom";

import "../TermsOfService/TermsOfService.scss";

interface IProps {}

const CommunityGuidelines: React.FC<IProps> = () => {
  return (
    <div className="TermsOfService">
      <Helmet>
        <title>Community Guidelines</title>
        <meta name="description" content="How we keep the Eager Dog community feed a welcoming place for dog sport people and their dogs." />
        <meta name="robots" content="index, follow" />
      </Helmet>
      <div className="genericInner">
        <p>Effective Date: October 30th, 2026</p>

        <h1>Community Guidelines</h1>

        <p>The Eager Dog community feed is a place to celebrate runs, share photos of your dogs, and keep up with the clubs and dogs you follow. These guidelines explain what we expect from everyone who posts, and what happens when a post falls short. They apply alongside our <Link to="/terms-of-service">Terms of Service</Link>.</p>

        <h2>What belongs here</h2>

        <ul>
          <li><b>Your dogs and your sport:</b> Run results, ribbons, training wins, event photos, and the everyday moments that make dog sports fun.</li>
          <li><b>Club news:</b> Clubs can share event updates, results, and announcements with their followers.</li>
          <li><b>Encouragement:</b> Celebrate other handlers and their dogs. A good day on the course is better when people cheer for it.</li>
        </ul>

        <h2>What does not belong here</h2>

        <p>When you report a post, you choose one of the reasons below. Here is what each one covers.</p>

        <ul>
          <li><b>Spam or advertising:</b> Repetitive posts, unsolicited promotions, affiliate links, or content posted mainly to sell something unrelated to the dog sports community. Clubs may promote their own events and services.</li>
          <li><b>Harassment or bullying:</b> Targeting a person, dog, club, or judge with insults, threats, or persistent unwanted attention. Disagreeing with a call is fine; attacking the person who made it is not.</li>
          <li><b>Hate or discrimination:</b> Content that attacks or demeans people based on race, ethnicity, religion, gender, sexual orientation, disability, age, or similar characteristics.</li>
          <li><b>Nudity or sexual content:</b> Sexually explicit or suggestive images or text. The feed is viewed by people of all ages.</li>
          <li><b>Violence, cruelty, or animal abuse:</b> Graphic violence, or content that shows, encourages, or makes light of mistreating animals. Posts that document a welfare concern in order to get help are welcome; please also contact the relevant authorities.</li>
          <li><b>False results or impersonation:</b> Posting results, titles, or placements that did not happen, or presenting yourself as another person, dog, or club.</li>
          <li><b>Something else:</b> Anything that makes the feed less safe or less welcoming and does not fit the reasons above. Tell us what you saw in the note.</li>
        </ul>

        <h2>Reporting a post</h2>

        <p>Every post has a menu (the <b>⋯</b> button) with a <b>Report post</b> option. Pick the reason that fits best and, if it helps, add a short note. Reports are private: the author is not told who reported them. Once you report a post, it is hidden from your feed.</p>

        <h2>What happens next</h2>

        <ul>
          <li><b>Review:</b> Our team looks at every report. A post that receives several reports from established accounts is hidden from the public feed automatically until we have reviewed it. The author can still see it, marked as under review.</li>
          <li><b>Automated screening:</b> New posts may also be checked automatically for content that clearly breaks these guidelines. A post that is held for a quick look stays visible to its author and appears to everyone else once it clears review.</li>
          <li><b>Outcomes:</b> If a post is fine, we restore it and no further action is taken. If it breaks these guidelines, we remove it and let the author know by email, including the reason. Repeated or serious violations can pause posting on an account for 30 days, and in serious cases lead to account restrictions under our Terms of Service.</li>
          <li><b>Club posts:</b> Club managers can remove posts made on behalf of their club at any time.</li>
        </ul>

        <h2>Questions or mistakes</h2>

        <p>If you believe a post was removed in error, or you have a concern that does not fit the report form, email us at <a href="mailto:support@eagerdog.com">support@eagerdog.com</a>. Include a link or description of the post so we can find it quickly.</p>

        <h2>Changes to these guidelines</h2>

        <p>We may update these guidelines as the community grows. Changes take effect when they are posted here.</p>
      </div>
    </div>
  );
};

export default CommunityGuidelines;
